import React from 'react';

/**
 * Skeleton loader components for progressive rendering / performance perception.
 * Usage:
 *   <SkeletonLoader type="card" count={4} />
 *   <SkeletonLoader type="table" rows={8} />
 *   <SkeletonLoader type="detail" />
 *   <SkeletonLoader type="stat" count={4} />
 *   <Skeleton className="h-4 w-32" />   ← bare bone block
 */

export function Skeleton({ className = '' }) {
  return (
    <div className={`skeleton-shimmer rounded ${className}`} />
  );
}

function StatSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <Skeleton className="h-3 w-28" />
        <Skeleton className="h-9 w-9 rounded-full" />
      </div>
      <Skeleton className="h-7 w-20 mb-1" />
      <Skeleton className="h-3 w-16" />
    </div>
  );
}

function CardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-3">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-4 w-16" />
      </div>
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="space-y-1.5">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
          <div className="space-y-1.5 items-end flex flex-col">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

function TableSkeleton({ rows = 8 }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex gap-4 px-4 py-3 border-b border-gray-200 bg-gray-50">
        {[40, 80, 60, 50, 70, 40].map((w, i) => (
          <Skeleton key={i} className={`h-3 w-${w === 40 ? '10' : w === 80 ? '20' : w === 60 ? '16' : w === 50 ? '12' : w === 70 ? '20' : '10'}`} />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-4 px-4 py-3.5 border-b border-gray-100 last:border-0">
          <Skeleton className="h-4 w-10" />
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
      ))}
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-6">
      {/* Header breadcrumb */}
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-4 w-4 rounded-full" />
        <Skeleton className="h-4 w-24" />
      </div>
      {/* Title area */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-24 rounded-lg" />
          <Skeleton className="h-9 w-24 rounded-lg" />
        </div>
      </div>
      {/* Progress bar */}
      <Skeleton className="h-16 w-full rounded-xl" />
      {/* Two-column content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <Skeleton className="h-5 w-32" />
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-4 w-40" />
            </div>
          ))}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <Skeleton className="h-5 w-32" />
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-4 w-36" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SkeletonLoader({ type = 'card', count = 1, rows = 8 }) {
  if (type === 'stat') {
    return (
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${Math.min(count, 4)} gap-4`}>
        {Array.from({ length: count }).map((_, i) => <StatSkeleton key={i} />)}
      </div>
    );
  }
  if (type === 'table') return <TableSkeleton rows={rows} />;
  if (type === 'detail') return <DetailSkeleton />;
  // card (default)
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {Array.from({ length: count }).map((_, i) => <CardSkeleton key={i} />)}
    </div>
  );
}
